import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '~/components/Button';
import Modal from '~/components/Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (email: string) => void;
  initialEmail?: string;
}

const EmailConfirmationModal = ({ isOpen, onClose, onConfirm, initialEmail = '' }: Props) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirm = () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setIsSubmitting(true);
    onConfirm(email);
  };

  const handleClose = () => {
    setError('');
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal
      title={t('stripe.email_confirmation.title')}
      message={t('stripe.email_confirmation.message')}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <div className="flex flex-col gap-4">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            placeholder={t('stripe.email_confirmation.placeholder')}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose} variant="secondary" disabled={isSubmitting}>
            {t('shared.cta.cancel')}
          </Button>
          <Button onClick={handleConfirm} variant="primary" disabled={isSubmitting}>
            {isSubmitting ? t('shared.actions.submitting') : t('shared.cta.continue')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EmailConfirmationModal;
